import React from 'react';
import moment from 'moment';
import { find, isEqual } from 'lodash';
import Datepicker from '../../common/Datepicker';
import ReactSelect from '../../common/ReactSelect';
import { ordinalSuffix } from '../../../helpers/NumberFormatter';

class Calendar extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
                period: 'daily',
                date: null,
                dayOfWeek: null,
                dayOfMonth: null,
                weekOfMonth: null,
                skipWeekend: false
        }
        
        this.setDate = this.setDate.bind(this);
        this.setSkipWeekend = this.setSkipWeekend.bind(this);
        this.setPeriod = this.setPeriod.bind(this);
    }
    
    _getNextRunDate() {
        const { period, date: startDate } = this.state;
        const today = moment().startOf('day');
        switch(period) {
            case 'one_time': {
                const next = moment(startDate);
                return next.isAfter(today) ? next : null; // null if the date has already passed
            }
            
            case 'daily': {
                return today.clone().add(1, 'day');
            }
            case 'weekly': {
                const target = moment(startDate).day(); // day-of-week from the selected date
                const next = today.clone().day(target);
                if(next.isSameOrBefore(today)) next.add(7, 'days');
                return next;
            }
            case 'bi_weekly': {
                // use start_date as the cycle anchor — find the next occurrence
                // that falls on the correct 2-week phase
                const anchor = moment(startDate).startOf('day');
                const target = anchor.day();
                let next = today.clone().day(target);
                if(next.isSameOrBefore(today)) next.add(7, 'days');
                // advance by weeks until we're on the same 2-week phase as anchor
                while(next.diff(anchor, 'days') % 14 !== 0) next.add(7, 'days');
                return next;
            }
            case 'monthly_day': {
                const dayOfMonth = moment(startDate).date();
                const next = today.clone().date(dayOfMonth);
                if(next.isSameOrBefore(today)) next.add(1, 'month').date(dayOfMonth);
                return next;
            }
            case 'monthly_day_of_week': {
                const dayOfWeek  = moment(startDate).day();
                const weekOfMonth = Math.ceil(moment(startDate).date() / 7);
                // find that weekday in the current month
                const findOccurrence = (monthMoment) => {
                    const first = monthMoment.clone().startOf('month').day(dayOfWeek);
                    if(first.month() !== monthMoment.month()) first.add(7, 'days');
                    return first.clone().add((weekOfMonth - 1) * 7, 'days');
                };
                let next = findOccurrence(today);
                if(next.isSameOrBefore(today)) next = findOccurrence(today.clone().add(1, 'month'));
                return next;
            }
            default:
                return null;
        }
    }
    
    getNextRunDate(){
        const next = this._getNextRunDate();
        if (next && this.state.skipWeekend) {
            const day = next.day();
            if (day === 6) next.add(2, 'days'); // Saturday → Monday
            if (day === 0) next.add(1, 'day');  // Sunday → Monday
        }
        return next;
    }
    
    initialize(mailingList) {
        const date = ( mailingList && mailingList.start_date) ? mailingList.start_date : moment().format('YYYY-MM-DD');
        if(mailingList) {
            const {skip_weekend, schedule_type} = mailingList;
            this.setState({
                period: schedule_type,
                skipWeekend: skip_weekend    
            }, ()=> {
                this.setDate(date);
            });
        }
        else 
            Promise.resolve().then(()=>this.setDate(date));
        
    }
    
    componentDidMount() {
        const { mailingList } = this.props;
        this.initialize(this.props.mailingList);
    }
    componentDidUpdate(prevProps) {
        if(!isEqual(prevProps.mailingList, this.props.mailingList) && this.props.initializeDate) {
            this.initialize(this.props.mailingList);    
        }
    }
    
    setDate(date) {
        const dateObject = moment(date);
        this.setState({
            date: dateObject.format('YYYY-MM-DD'),
            dayOfWeek: dateObject.format('dddd'),
            dayOfMonth: dateObject.format('D'),
            weekOfMonth: Math.ceil(dateObject.date() / 7)
        }, ()=>{
            this.props.onChange(this.state)
        });
    }
    
    setPeriod(period) {
        if(period) {
            this.setState({period}, ()=>{this.props.onChange(this.state)});
        }
    }
    
    setSkipWeekend(skipWeekend) {
        this.setState({skipWeekend}, ()=>{this.props.onChange(this.state)});
    }

    
    
    
    renderOptions(readonly) {
        let { period, skipWeekend } = this.state;
        let options = [ 
            {label: 'Daily', value: 'daily'},
            {label: `Weekly on ${this.state.dayOfWeek}`, value: 'weekly'},
            {label: `Every 2nd week on ${this.state.dayOfWeek}`, value: 'bi_weekly'},
            {label: `Monthly on every ${ordinalSuffix(this.state.weekOfMonth)} ${this.state.dayOfWeek}`, value: 'monthly_day_of_week'},
            {label: `Monthly on ${ordinalSuffix(this.state.dayOfMonth)}`, value: 'monthly_day'},
            {label: 'One Time', value: 'one_time'}
        ],
        value = find(options, option=>option.value == period);
        
        return <ReactSelect isDisabled={readonly} className="period repeat-on-period" id="mailing_list_calendar" options={options} value={value} onChange={(option)=>this.setPeriod(option.value)} />
    }
    
    render() {
        const { readonly = false, showNextRun = false} = this.props;
        const { skipWeekend } = this.state;
        const nextRun = showNextRun ? this.getNextRunDate() : undefined;
        return <div className="mailing-list-calendar-holder inline-datepicker">
            <Datepicker datepickerPosition="right" className="calendar" date={this.state.date} dateChange={(date)=>this.setDate(date)} readonly={readonly} />
            <div className='skip-weekend-holder'>
                <label for="skip_weekend">Skip Weekend</label>
                <input type="checkbox" id="skip_weekend" checked={skipWeekend} onChange={(e)=>this.setSkipWeekend(e.target.checked)} /> 
            </div>
            {this.renderOptions(readonly)}
            {nextRun && <span className="next-run-date">Next run: {nextRun.format('YYYY-MM-DD')}</span>}
        </div>
    }
}

export default Calendar;